import { useCallback, useEffect, useRef, useState } from "react";
import { memberRouter } from "../../app/runtime";
import { authApi } from "../shared/api";
import { navigate } from "../shared/ui";

interface Destination { readonly to: string; readonly replace?: boolean }

/** A password-change proof belongs only to this mounted visit; it is never persisted. */
export function usePasswordChangeExitGuard(onPageExit: () => void) {
  const proof = useRef<string | undefined>(undefined);
  const dirty = useRef(false);
  const finished = useRef(false);
  const mounted = useRef(false);
  const generation = useRef(0);
  const leaving = useRef(false);
  const pageExit = useRef(onPageExit);
  const abandonRequest = useRef(authApi.abandonPasswordChange);
  pageExit.current = onPageExit;
  const [destination, setDestination] = useState<Destination>();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  const currentUrl = useRef(`${window.location.pathname}${window.location.search}${window.location.hash}`);

  const abandonQuietly = useCallback((token: string) => {
    void abandonRequest.current(token).catch(() => undefined);
  }, []);
  const dispose = useCallback(() => {
    generation.current += 1;
    const token = proof.current;
    proof.current = undefined;
    dirty.current = false;
    if (!finished.current && token) abandonQuietly(token);
  }, [abandonQuietly]);

  useEffect(() => {
    mounted.current = true;
    const unblock = memberRouter.blockNavigation((to, options) => {
      if (!dirty.current || finished.current || to === currentUrl.current) return true;
      setError(undefined);
      setDestination({ to, replace: options.replace });
      return false;
    });
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty.current || finished.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    const pageHide = () => { dispose(); pageExit.current(); };
    window.addEventListener("beforeunload", beforeUnload);
    window.addEventListener("pagehide", pageHide);
    return () => {
      mounted.current = false;
      unblock();
      window.removeEventListener("beforeunload", beforeUnload);
      window.removeEventListener("pagehide", pageHide);
      // StrictMode immediately repeats setup; only an actual unmount disposes the proof.
      queueMicrotask(() => { if (!mounted.current) dispose(); });
    };
  }, [dispose]);

  const markDirty = useCallback(() => { dirty.current = true; }, []);
  const beginVerification = useCallback(() => {
    dirty.current = true;
    generation.current += 1;
    return generation.current;
  }, []);
  const acceptProof = useCallback((token: string, expectedGeneration: number) => {
    if (!mounted.current || finished.current || expectedGeneration !== generation.current) {
      abandonQuietly(token);
      return false;
    }
    proof.current = token;
    dirty.current = true;
    return true;
  }, [abandonQuietly]);
  const isCurrent = useCallback((expectedGeneration: number) => mounted.current && expectedGeneration === generation.current && !finished.current, []);
  const currentGeneration = useCallback(() => generation.current, []);
  const finish = useCallback(() => {
    finished.current = true;
    proof.current = undefined;
    dirty.current = false;
    generation.current += 1;
  }, []);
  const reset = useCallback(() => { dispose(); }, [dispose]);
  const stay = () => { if (!leaving.current) { setDestination(undefined); setError(undefined); } };
  const leave = async () => {
    if (!destination || leaving.current) return;
    leaving.current = true;
    setPending(true); setError(undefined);
    try {
      if (proof.current) await abandonRequest.current(proof.current);
      proof.current = undefined;
      dirty.current = false;
      generation.current += 1;
      pageExit.current();
      navigate(destination.to, destination.replace);
      if (mounted.current) setDestination(undefined);
    } catch {
      if (mounted.current) setError("비밀번호 변경을 종료하지 못했습니다. 현재 화면에서 다시 시도해 주세요.");
    } finally {
      leaving.current = false;
      if (mounted.current) setPending(false);
    }
  };
  return { markDirty, beginVerification, acceptProof, isCurrent, currentGeneration, finish, reset, destination, pending, error, stay, leave };
}
